/**
 * GTM Enhanced Ecommerce helpers, adapted to the ON AIR `tunnel` shape.
 *
 * Prices are expressed in euros (Resamania amounts are stored in cents).
 * For the main offer we also keep the legacy `price_tax_exc` / `price_tax_inc`
 * keys that ON AIR historically emitted, so existing GTM tags / Looker fields
 * keep resolving. These are additive: no field is renamed.
 */

const toEuros = (cents) => (typeof cents === "number" ? cents / 100 : 0);

/**
 * Format the main subscription offer for ecommerce.
 * @param {Object} offer - tunnel.offer
 * @param {string} clubName - tunnel.club?.name (item_brand)
 * @returns {Object|null}
 */
export const formatOfferForEcommerce = (offer, clubName) => {
  if (!offer) return null;

  return {
    item_id: offer["@id"] || offer.offer?.product,
    item_name: offer.name,
    item_category: "subscription",
    item_brand: clubName,
    // price keeps its historical TE semantic; the whole item is in euros so no
    // cents/euros mix (legacy keys kept for existing ON AIR tags, also in euros).
    price: toEuros(offer.offer?.registrationFeeTE),
    quantity: 1,
    price_tax_exc: toEuros(offer.offer?.registrationFeeTE),
    price_tax_inc: toEuros(offer.offer?.registrationFeeTI),
  };
};

/**
 * Format options (tunnel.options is an array).
 * @param {Array} options
 * @param {string} clubName
 * @returns {Array}
 */
export const formatOptionsForEcommerce = (options, clubName) => {
  if (!Array.isArray(options) || !options.length) return [];

  return options.map((option, index) => ({
    item_id: option["@id"] || option.offer?.product,
    item_name: option.name,
    item_category: "option",
    item_brand: clubName,
    price: toEuros(option.offer?.registrationFeeTE ?? option.offer?.priceTI),
    quantity: 1,
    index,
  }));
};

/**
 * Format regulars/equipment (tunnel.regulars is an object keyed by id:
 * { [id]: { regular, count } }).
 * @param {Object} regulars
 * @param {string} clubName
 * @returns {Array}
 */
export const formatRegularsForEcommerce = (regulars, clubName) => {
  if (!regulars || typeof regulars !== "object") return [];

  return Object.values(regulars)
    .filter((r) => r && r.count > 0 && r.regular)
    .map(({ regular, count }) => ({
      item_id: regular["@id"] || regular.offer?.product,
      item_name: regular.name,
      item_category: "equipment",
      item_brand: clubName,
      price: toEuros(regular.offer?.registrationFeeTE ?? regular.offer?.priceTI),
      quantity: count,
    }));
};

/**
 * Build the full cart (offer + options + regulars) for the purchase event.
 * @param {Object} tunnel
 * @returns {Array}
 */
export const formatCartForEcommerce = (tunnel) => {
  const items = [];
  const clubName = tunnel.club?.name || "";

  const offerItem = formatOfferForEcommerce(tunnel.offer, clubName);
  if (offerItem) items.push(offerItem);

  const options = formatOptionsForEcommerce(tunnel.options, clubName);
  const regulars = formatRegularsForEcommerce(tunnel.regulars, clubName);
  items.push(...options, ...regulars);

  // The ON AIR offer object carries no subscription unit price: amounts are
  // computed server-side into the sale (cf. SubscriptionSummary, "mon
  // abonnement" = sales.totalTI - registrationFeeTI). registrationFeeTE/TI alone
  // is 0 for most offers, which is why the item price was 0. When the cart is a
  // single offer (no options/regulars) and the sale is known, use the sale
  // amounts so the item price reflects what the customer actually pays. This
  // only runs at purchase time; add_to_cart (no sale yet) and multi-item carts
  // keep the existing fallback, so nothing regresses.
  if (offerItem && !options.length && !regulars.length && tunnel.sales) {
    const ti = tunnel.sales.totalTI;
    const te = tunnel.sales.totalTE;
    if (typeof ti === "number") {
      offerItem.price = ti / 100;
      offerItem.price_tax_inc = ti / 100;
      if (typeof te === "number") offerItem.price_tax_exc = te / 100;
    }
  }

  return items;
};

/**
 * Total conversion value, in euros.
 * NOTE (MSDS-962, out of scope here): what value to report when totalTI === 0
 * is a product decision. We only apply the mechanical cents-to-euros conversion.
 * @param {Object} tunnel
 * @returns {number}
 */
export const getTotalValue = (tunnel) => toEuros(tunnel.sales?.totalTI);
